"use client";

import { useCallback, useState } from "react";
import { leftArrowIcon } from "../../assets";
import {
  updatePassword,
  verifyPassword,
} from "../../lib/services/auth-service";
import { setLoggedinUser } from "../../lib/util";
import { useToken } from "../../lib/context/auth-context";
import toast from "react-hot-toast";
import { logInUser } from "../slice/auth-slice";
import { useAppDispatch } from "../slice/hooks";

export const UpdatePassword = ({
  setIsChangingPassword,
  loggedInUser,
  ipDetails,
  handleLoginSuccess,
}) => {
  const [error, setError] = useState("");
  const { token } = useToken();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    type: "single",
    confirmNewPassword: "",
  });
  const toggleVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  }, []);

  const handlePasswordUpdate = useCallback(
    async (e) => {
      e.preventDefault();
      if (formData.currentPassword.length < 7) {
        setError("Current password is at least 8 characters long");
        return;
      }
      if (
        formData.newPassword.length < 7 ||
        formData.confirmNewPassword.length < 7
      ) {
        setError("New password must be at least 8 characters long");
        return;
      }

      if (formData.newPassword !== formData.confirmNewPassword) {
        setError("Passwords do not match");
        return;
      }

      try {
        setIsLoading(true);

        await verifyPassword(
          token,
          loggedInUser?.user.userId,
          {
            password: formData.currentPassword,
          },
          ipDetails
        );

        const res = await updatePassword(
          token,
          loggedInUser?.user.userId,
          {
            oldPassword: formData.currentPassword,
            newPassword: formData.newPassword,
            type: formData.type,
            confirmPassword: formData.confirmNewPassword,
          },
          ipDetails
        );

        const {
          user,
          token: newToken,
          tokenExpiryDate,
          sessionId,
          tokenType,
        } = res.data;

        const newLoggedInUser = {
          sessionId,
          tokenExpiryDate,
          newToken,
          user: user,
        };
        setLoggedinUser(newLoggedInUser);
        dispatch(
          logInUser({
            loggedInUser: newLoggedInUser,
            authenticated: true,
            token: newToken,
          })
        );
        toast.success("Password updated successfully");

        setIsLoading(false);
        setIsChangingPassword(false);
      } catch (err) {
        if (err?.message == "Invalid password") {
          const errorMessage = "Current password is incorrect";
          setError(errorMessage);
        }
        toast.error(err?.message || "Failed to update password");
      } finally {
        setIsLoading(false);
      }
    },
    [formData, loggedInUser]
  );

  return (
    <>
      <div className="nav-header">
        <h3>
          Update Password {isLoading && <span className="only-spinner" />}
        </h3>

        <button
          className="nav-header-button"
          onClick={() => setIsChangingPassword(false)}
        >
          <span>{leftArrowIcon}</span>
          <span className="psw-btn">Back to Profile</span>
        </button>
      </div>

      <form className="nav-body" onSubmit={handlePasswordUpdate}>
        {error && <div className="error-message">{error}</div>}
        <div className="input-box">
          <label htmlFor="password">Current Password</label>
          <div className="input-text">
            <input
              type={passwordVisibility.current ? "text" : "password"}
              name="currentPassword"
              placeholder="Enter current password"
              onChange={handleFormChange}
              value={formData.currentPassword}
              disabled={isLoading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => toggleVisibility("current")}
              aria-label={
                passwordVisibility.current ? "Hide password" : "Show password"
              }
            >
              <i
                className={`fa ${
                  passwordVisibility.current ? "fa-eye-slash" : "fa-eye"
                }`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <div className="input-box">
          <label htmlFor="password">New Password</label>
          <div className="input-text">
            <input
              type={passwordVisibility.new ? "text" : "password"}
              name="newPassword"
              placeholder="Enter new password"
              onChange={handleFormChange}
              value={formData.newPassword}
              disabled={isLoading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => toggleVisibility("new")}
              aria-label={
                passwordVisibility.new ? "Hide password" : "Show password"
              }
            >
              <i
                className={`fa ${
                  passwordVisibility.new ? "fa-eye-slash" : "fa-eye"
                }`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <div className="input-box">
          <label htmlFor="password">Confirm New Password</label>
          <div className="input-text">
            <input
              type={passwordVisibility.confirm ? "text" : "password"}
              name="confirmNewPassword"
              placeholder="Confirm new password"
              value={formData.confirmNewPassword}
              onChange={handleFormChange}
              disabled={isLoading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => toggleVisibility("confirm")}
              aria-label={
                passwordVisibility.confirm ? "Hide password" : "Show password"
              }
            >
              <i
                className={`fa ${
                  passwordVisibility.confirm ? "fa-eye-slash" : "fa-eye"
                }`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>

        <button
          type="submit"
          className={
            isLoading ? "auth-submit-button disabled" : "auth-submit-button"
          }
          disabled={isLoading}
        >
          Update Password
        </button>
      </form>
    </>
  );
};
